import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { LESSON_ACTIVITIES } from 'global/constants';
import { getInteractiveContentType } from 'approot/activity-builder/activity-builder.utils';
import { useCheckAnswer } from 'approot/activity-builder/check-answer/check-answer.hooks';
import { checkAnswerVar } from 'approot/activity-builder/check-answer/check-answer.apollo';
import {
  isAnswerCorrect,
  resetComponentAnswer,
} from 'approot/activity-builder/check-answer/check-answer.utils';
import { SecondaryButton } from 'approot/shared/styles/buttons/secondary-button.style';
import Text from 'approot/shared/styles/typography/text';
import { Box } from 'approot/shared/design-system/box/box.style';
import {
  MultipleChoiceStudentCheckAnswerDivider,
  MultipleChoiceStudentCheckAnswerButton,
} from './multiple-choice-student-check-answer.style';
import MultipleChoiceStudentHint from './multiple-choice-student-hint';
import ImageEmojiPopper from 'approot/shared/images/emojis/image-emoji-popper';

type Props = {
  id: string;
  isFreeAnswer: boolean;
  moreInformation: string | null;
  onSubmitAnswer: () => void;
};

const MultipleChoiceStudentCheckAnswer = ({
  id,
  isFreeAnswer,
  moreInformation,
  onSubmitAnswer,
}: Props) => {
  const [showHint, setShowHint] = useState<boolean>(true);

  const { checkAnswerData, currentAnswerData, hasAnswered } = useCheckAnswer({
    componentId: id,
  });

  const componentAnswers = checkAnswerData[id];

  const canShowAnswer = useMemo(
    () => getInteractiveContentType() === LESSON_ACTIVITIES && !isFreeAnswer,
    [isFreeAnswer]
  );

  const selectedAnswers = componentAnswers?.multiAnswer?.answerRefs || [];

  return (
    <Box $alignItems="center" $gap={8}>
      <MultipleChoiceStudentCheckAnswerDivider />
      {canShowAnswer &&
        hasAnswered &&
        currentAnswerData &&
        isAnswerCorrect(currentAnswerData!) && (
          <Box $padding={8} $gap={8} $alignItems="center">
            <Text>Well done!</Text>
            <ImageEmojiPopper width={16} aria-hidden />
          </Box>
        )}

      {hasAnswered && !canShowAnswer && (
        <Box $alignItems="center" $gap={8}>
          <Text>Answer submitted </Text>
          <SecondaryButton
            type="button"
            $borderRadius={40}
            onClick={() => resetComponentAnswer(id, checkAnswerData)}
          >
            Change Answer
          </SecondaryButton>
        </Box>
      )}

      {canShowAnswer &&
        hasAnswered &&
        currentAnswerData &&
        !isAnswerCorrect(currentAnswerData) &&
        moreInformation &&
        showHint &&
        document.getElementById(`check-answer-${id}`) &&
        createPortal(
          <MultipleChoiceStudentHint
            hint={moreInformation}
            onClose={() => setShowHint(false)}
          />,
          document.getElementById(`check-answer-${id}`)!
        )}

      {canShowAnswer &&
      hasAnswered &&
      currentAnswerData &&
      !isAnswerCorrect(currentAnswerData) ? (
        <MultipleChoiceStudentCheckAnswerButton
          type="button"
          $borderRadius={40}
          onClick={() => {
            resetComponentAnswer(id, checkAnswerData);
          }}
        >
          Try Again
        </MultipleChoiceStudentCheckAnswerButton>
      ) : !hasAnswered ? (
        <MultipleChoiceStudentCheckAnswerButton
          type="button"
          disabled={selectedAnswers.length === 0}
          onClick={() => {
            if (componentAnswers) {
              checkAnswerVar({
                ...checkAnswerData,
                [id]: {
                  ...componentAnswers,
                  hasChecked: true,
                },
              });
              onSubmitAnswer();
            }
          }}
        >
          Submit Answer
        </MultipleChoiceStudentCheckAnswerButton>
      ) : null}
    </Box>
  );
};

export default MultipleChoiceStudentCheckAnswer;
